import { useState, type ReactNode } from 'react';
import { PlainLink } from '@paige/ui';
import { useFormat, type Formatter } from '@paige/format';
import type { FlowLinks } from '../flow/FlowDetail';
import { IdChip } from '../ops/IdChip';
import { OpsCompanyName } from '../ops/OpsCompanyName';
import type { FileStory, StoryEvent, StoryStep } from './timeline-types';
import {
  deliveryLines,
  gapBefore,
  stateWord,
  stepFacts,
  stepHeld,
  stepTitle,
  stepTone,
  stepTrouble,
  storyLine,
  type StoryTone,
} from './timeline-words';
import './timeline.css';

export interface FileStoryViewProps {
  story: FileStory;
  links: FlowLinks;
  /** Every ledger event, once asked for; undefined before, null while they load. */
  events: StoryEvent[] | null | undefined;
  onShowEvents: () => void;
}

const MARK: Record<StoryTone, string> = {
  ok: 'M5 10.5l3.2 3.2L15 7',
  live: 'M10 5.5v4.8l3 1.8',
  wait: 'M7.5 6.5v7m5-7v7',
  stop: 'M10 5.5v5.5m0 2.6v.1',
  off: 'M6 10h8',
};

/** The mark on the story's line: a tick, a clock hand, a pause, a stop or a dash. A shape as well as a colour. */
function Mark({ tone }: { tone: StoryTone }): ReactNode {
  return (
    <span className={`tl-mark tl-mark--${tone}`} aria-hidden="true">
      <svg viewBox="0 0 20 20" width="20" height="20">
        <path
          d={MARK[tone]}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

function took(f: Formatter, step: StoryStep): string | null {
  if (!step.endedAt) return null;
  const seconds = Math.round((Date.parse(step.endedAt) - Date.parse(step.at)) / 1000);
  return seconds >= 1 ? f.duration(seconds) : null;
}

/** What a step says under its title: its facts, why it stopped, and how often it was put back to wait. */
function StepBody({
  step,
  story,
  f,
}: {
  step: StoryStep;
  story: FileStory;
  f: Formatter;
}): ReactNode {
  const length = took(f, step);
  // The pill already says how long it took; the work time is said only when it differs (a wait, a retry).
  const facts = stepFacts(f, step, story).filter((fact) => fact !== `${length} of work`);
  const trouble = stepTrouble(step);
  const held = stepHeld(f, step);
  const deliveries = deliveryLines(f, step, story);
  return (
    <div className="tl-step__body">
      <h3 className="tl-step__title">
        {stepTitle(step, story)}
        {length ? <span className="tl-step__took">{length}</span> : null}
      </h3>
      {facts.length > 0 ? <p className="tl-step__facts">{facts.join(' · ')}</p> : null}
      {trouble ? <p className="tl-step__trouble">{trouble}</p> : null}
      {held ? <p className="tl-step__held">{held}</p> : null}
      {deliveries.length > 0 ? (
        <ul className="tl-step__deliveries" aria-label="Each delivery">
          {deliveries.map((d, i) => (
            <li key={`${d.at}:${i}`} data-failed={d.failed}>
              <time dateTime={d.at}>{f.time(d.at)}</time> {d.text}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

interface StepProps {
  step: StoryStep;
  previous: StoryStep | undefined;
  story: FileStory;
  f: Formatter;
}

function Step({ step, previous, story, f }: StepProps): ReactNode {
  const tone = stepTone(step);
  const gap = gapBefore(step, previous);
  return (
    <li className="tl-step" data-step={step.key} data-tone={tone}>
      {gap > 0 ? <p className="tl-gap">{f.duration(gap)} later</p> : null}
      <div className="tl-step__row">
        <time className="tl-step__when" dateTime={step.at}>
          {f.time(step.at)}
        </time>
        <Mark tone={tone} />
        <StepBody step={step} story={story} f={f} />
      </div>
    </li>
  );
}

/** Where it stands, and its ids: each copies whole. */
function Side({ story }: { story: FileStory }): ReactNode {
  const state = stateWord(story.state);
  return (
    <div className="tl-head__side">
      <span className={`tl-state tl-state--${state.tone}`} data-state={story.state}>
        {state.word}
      </span>
      <span className="tl-head__ids">
        {story.documentId ? (
          <>
            document <IdChip id={story.documentId} what="document" />
          </>
        ) : null}
        batch <IdChip id={story.batchId} what="batch" />
      </span>
    </div>
  );
}

function Head({
  story,
  links,
  f,
}: {
  story: FileStory;
  links: FlowLinks;
  f: Formatter;
}): ReactNode {
  const name = story.fileName ?? (story.subject === 'document' ? 'A document' : 'A file');
  return (
    <header className="tl-head">
      <div className="tl-head__main">
        <h2 className="tl-head__name">
          {name}
          {story.files > 1 ? (
            <span className="tl-head__more"> and {f.int(story.files - 1)} more</span>
          ) : null}
        </h2>
        <p className="tl-head__whose">
          <OpsCompanyName
            company={story.company}
            href={links.company(story.company.slug)}
            Link={links.Link}
          />
          <span>{story.jobName ?? 'No job'}</span>
          {story.typeName ? <span>{story.typeName}</span> : null}
        </p>
        <p className="tl-head__line">{storyLine(f, story)}</p>
      </div>
      <Side story={story} />
    </header>
  );
}

/** How many of a file's documents are listed before "Show all". */
const DOCUMENTS_SHOWN = 12;

function Documents({ story, links }: { story: FileStory; links: FlowLinks }): ReactNode {
  const Link = links.Link ?? PlainLink;
  const [all, setAll] = useState(false);
  if (story.documents.length === 0) return null;
  const count = story.documentCount === 1 ? '1 document' : `${story.documentCount} documents`;
  const shown = all ? story.documents : story.documents.slice(0, DOCUMENTS_SHOWN);
  return (
    <section className="tl-docs" aria-label="Documents this file became">
      <h3 className="tl-section">It became {count}</h3>
      <ul>
        {shown.map((d) => (
          <li key={d.id}>
            <Link href={links.timeline(d.id)}>{d.typeName ?? 'A document'}</Link>
            <span className="tl-docs__meta">
              {d.pages} {d.pages === 1 ? 'page' : 'pages'} · {stateWord(d.state).word}
            </span>
          </li>
        ))}
      </ul>
      {shown.length < story.documents.length ? (
        <button type="button" className="tl-docs__more" onClick={() => setAll(true)}>
          Show all {story.documents.length}
        </button>
      ) : null}
    </section>
  );
}

function RawLine({ event: e, f }: { event: StoryEvent; f: Formatter }): ReactNode {
  return (
    <li data-ok={e.ok}>
      <time dateTime={e.at}>{f.time(e.at)}</time>
      <span className="tl-raw__stage">{e.stage}</span>
      <span>
        {e.label}
        {e.detail ? <span className="tl-raw__detail"> {e.detail}</span> : null}
      </span>
      {e.traceId ? <IdChip id={e.traceId} what="trace" /> : <span />}
    </li>
  );
}

interface RawProps {
  events: StoryEvent[] | null | undefined;
  onShow: () => void;
  f: Formatter;
}

function Raw({ events, onShow, f }: RawProps): ReactNode {
  return (
    <details
      className="tl-raw"
      onToggle={(e) => (e.currentTarget.open && events === undefined ? onShow() : undefined)}
    >
      <summary>Every ledger event, for engineering</summary>
      {!events ? (
        <p className="tl-raw__note">Reading the events</p>
      ) : (
        <ol className="tl-raw__list">
          {events.map((e) => (
            <RawLine key={e.id} event={e} f={f} />
          ))}
        </ol>
      )}
    </details>
  );
}

/**
 * Level 0, screen 5: what happened to one file, top to bottom: it arrived, each step with its time and how long it
 * took, where it waited and why, the deliveries and what was charged. The ledger's own events sit under it for
 * engineering.
 */
export function FileStoryView({
  story,
  links,
  events,
  onShowEvents,
}: FileStoryViewProps): ReactNode {
  const f = useFormat();
  return (
    <article className="tl" data-story={story.documentId ?? story.batchId}>
      <Head story={story} links={links} f={f} />
      <Documents story={story} links={links} />
      <ol className="tl-steps" aria-label="What happened">
        <li className="tl-day">{f.date(story.receivedAt)}</li>
        {story.steps.map((step, i) => (
          <Step
            key={`${step.key}-${i}`}
            step={step}
            previous={story.steps[i - 1]}
            story={story}
            f={f}
          />
        ))}
      </ol>
      {story.truncated ? (
        <p className="tl-raw__note">
          This file has more history than one page reads; the story is from its first 6,000 events.
        </p>
      ) : null}
      <Raw events={events} onShow={onShowEvents} f={f} />
    </article>
  );
}
